<template>
  <!-- Estesoft.App CurrencyRatesBarComponent'in Vega karşılığı; veri ve davranış aynı. Kur listesi boşken
       (ana para birimi dışında aktif para birimi yok) düğme hiç görünmez, hata varsa yeniden denensin diye görünür. -->
  <li v-if="rates.length || hasError" class="nav-item position-relative">
    <button ref="navbarToggle"
            type="button"
            class="vega-hbtn"
            :class="{ 'is-open': navbarDropdownOpen }"
            :aria-label="$t('PermissionDashboard.CurrencyRates.Title')"
            :title="$t('PermissionDashboard.CurrencyRates.Title')"
            aria-haspopup="true"
            :aria-expanded="navbarDropdownOpen ? 'true' : 'false'"
            @click="toggleNavbarDropdown">
      <i class="fa-regular fa-dollar-sign" aria-hidden="true"></i>
    </button>
    <div v-show="navbarDropdownOpen" ref="navbarMenu" class="vega-hmenu">
      <div class="vega-hmenu__head">
        <div class="vega-rate__head-text">
          <h2 class="vega-hmenu__title">{{ $t('PermissionDashboard.CurrencyRates.Title') }}</h2>
          <span v-if="lastSyncDate" class="vega-rate__sync" :title="$dayjs(lastSyncDate).format('DD.MM.YYYY HH:mm')">
            {{ $t('PermissionDashboard.CurrencyRates.LastUpdated') }}: {{ $dayjs(lastSyncDate).fromNow() }}
          </span>
        </div>
        <button type="button"
                class="vega-hbtn vega-rate__refresh"
                :disabled="isLoading"
                :title="$t('PermissionDashboard.CurrencyRates.Refresh')"
                :aria-label="$t('PermissionDashboard.CurrencyRates.Refresh')"
                @click="loadRates()">
          <i class="fa-regular fa-arrows-rotate" :class="{ 'fa-spin': isLoading }" aria-hidden="true"></i>
        </button>
      </div>

      <div v-if="hasError" class="vega-hmenu__empty vega-rate__error">
        <i class="fa-regular fa-circle-exclamation" aria-hidden="true"></i>
        <span>{{ $t('PermissionDashboard.CurrencyRates.Error') }}</span>
      </div>

      <template v-else>
        <div class="vega-hmenu__scroll">
          <div v-for="item in rates" :key="item.id" class="vega-rate">
            <span class="vega-rate__pair">{{ item.toCurrencySymbol }}/{{ item.fromCurrencySymbol }}</span>
            <strong class="vega-rate__value">{{ formatRate(item.parity) }}</strong>
          </div>
        </div>

        <template v-if="selectedRate">
          <div class="vega-hmenu__sep"></div>
          <div class="vega-rate__label">{{ $t('PermissionDashboard.CurrencyRates.ConverterTitle') }}</div>

          <div class="vega-rate__converter">
            <div class="vega-rate__row">
              <input v-model.number="converterAmount"
                     type="number"
                     min="0"
                     step="any"
                     inputmode="decimal"
                     class="form-control vega-rate__amount"
                     :aria-label="$t('PermissionDashboard.CurrencyRates.Amount')" />
              <select v-if="!isReverseConversion"
                      v-model="selectedRateId"
                      class="form-select vega-rate__unit"
                      :aria-label="$t('PermissionDashboard.CurrencyRates.Currency')">
                <option v-for="item in rates" :key="item.id" :value="item.id">{{ item.toCurrencySymbol }}</option>
              </select>
              <span v-else class="vega-rate__unit vega-rate__unit--static">{{ selectedRate.fromCurrencySymbol }}</span>
            </div>

            <button type="button"
                    class="vega-hbtn vega-rate__swap"
                    :title="$t('PermissionDashboard.CurrencyRates.SwitchDirection')"
                    :aria-label="$t('PermissionDashboard.CurrencyRates.SwitchDirection')"
                    @click="isReverseConversion = !isReverseConversion">
              <i class="fa-regular fa-arrow-right-arrow-left" aria-hidden="true"></i>
            </button>

            <div class="vega-rate__row">
              <span class="vega-rate__result">{{ convertedAmount === null ? '-' : formatRate(convertedAmount) }}</span>
              <select v-if="isReverseConversion"
                      v-model="selectedRateId"
                      class="form-select vega-rate__unit"
                      :aria-label="$t('PermissionDashboard.CurrencyRates.Currency')">
                <option v-for="item in rates" :key="item.id" :value="item.id">{{ item.toCurrencySymbol }}</option>
              </select>
              <span v-else class="vega-rate__unit vega-rate__unit--static">{{ selectedRate.fromCurrencySymbol }}</span>
            </div>
          </div>
        </template>
      </template>
    </div>
  </li>
</template>

<script>
  import commonService from '../../services/common';
  import navbarDropdown from './navbars/navbarDropdown';

  export default {
    name: 'CurrencyRates',
    mixins: [navbarDropdown],
    data() {
      return {
        rates: [],
        isLoading: false,
        hasError: false,
        converterAmount: 1,
        selectedRateId: null,
        isReverseConversion: false
      }
    },
    computed: {
      selectedRate() {
        return this.rates.find(item => item.id === this.selectedRateId) || null;
      },
      convertedAmount() {
        if (!this.selectedRate || this.converterAmount === '') return null;

        const amount = Number(this.converterAmount);
        const parity = Number(this.selectedRate.parity);
        if (!Number.isFinite(amount) || !Number.isFinite(parity) || parity === 0) return null;

        return this.isReverseConversion ? amount / parity : amount * parity;
      },
      lastSyncDate() {
        const rateDates = this.rates.map(item => this.parseRateDate(item.date)).filter(Boolean);
        const fallbackDates = this.rates.map(item => this.parseRateDate(item.modified)).filter(Boolean);
        const candidates = rateDates.length ? rateDates : fallbackDates;
        if (!candidates.length) return null;

        return new Date(Math.max(...candidates.map(date => date.getTime())));
      }
    },
    mounted() {
      this.loadRates();
    },
    methods: {
      loadRates() {
        this.isLoading = true;
        this.hasError = false;

        return commonService.getCurrencyRates()
          .then(rates => {
            this.rates = Array.isArray(rates) ? rates : [];
            if (!this.rates.some(item => item.id === this.selectedRateId))
              this.selectedRateId = this.rates[0] ? this.rates[0].id : null;
          })
          .catch(() => {
            this.hasError = true;
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
      formatRate(value) {
        const number = Number(value);
        if (!Number.isFinite(number)) return '-';

        return number.toLocaleString(this.$i18n.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      // Sunucu tarihi şube saatine çevrilmiş "dd.MM.yyyy HH:mm" metni olarak gönderir.
      parseRateDate(value) {
        if (!value) return null;

        if (typeof value === 'string') {
          const localDate = value.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})$/);
          if (localDate) {
            const [, day, month, year, hour, minute] = localDate;
            return new Date(year, Number(month) - 1, day, hour, minute);
          }
        }

        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
    }
  }
</script>

<style src="../../assets/css/header-menu.css"></style>
